import React, { useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Modal, Spin, notification } from 'antd'
import { SCRAPING_TAGS } from '../../../graphql/queries'
import {
  CREATE_SCRAPING_TAG_GROUP,
  UPDATE_SCRAPING_TAG_GROUP,
} from '../../../graphql/mutations'
import { transformToUpsertScrapingTagGroup } from '../../../graphql/transformers'
import { ScrapingTagGroupEditorForm } from '../../forms'
import PropTypes from 'prop-types'

const ScrapingTagGroupEditorModal = ({
  scrapingTagGroup,
  refetchScrapingTagGroups,
  isModalOpen,
  closeModal,
}) => {
  const {
    data: { scrapingTags } = {},
    loading: isLoadingScrapingTags,
  } = useQuery(SCRAPING_TAGS, {
    fetchPolicy: 'no-cache',
  })

  const [
    createScrapingTagGroup,
    { loading: isCreatingScrapingTagGroup },
  ] = useMutation(CREATE_SCRAPING_TAG_GROUP, {
    onCompleted() {
      refetchScrapingTagGroups()
      closeModal()
      notification.open({
        message: 'Created scraping tag group.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to create scraping tag group.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const [
    updateScrapingTagGroup,
    { loading: isUpdatingScrapingTagGroup },
  ] = useMutation(UPDATE_SCRAPING_TAG_GROUP, {
    onCompleted() {
      refetchScrapingTagGroups()
      closeModal()
      notification.open({
        message: 'Updated scraping tag group.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to update scraping tag group.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const handleSubmit = useCallback(
    tagGroup => {
      const variables = transformToUpsertScrapingTagGroup(tagGroup)

      return tagGroup.id
        ? updateScrapingTagGroup({ variables })
        : createScrapingTagGroup({
            variables,
          })
    },
    [createScrapingTagGroup, updateScrapingTagGroup],
  )

  return (
    <Modal
      title={scrapingTagGroup?.id ? 'Edit tag group' : 'Create tag group'}
      visible={isModalOpen}
      footer={null}
      onCancel={closeModal}
      destroyOnClose
    >
      {isLoadingScrapingTags ? (
        <Spin tip="Loading..." />
      ) : (
        <ScrapingTagGroupEditorForm
          scrapingTagGroup={scrapingTagGroup}
          tags={scrapingTags}
          onSubmit={handleSubmit}
          isSubmitting={
            isCreatingScrapingTagGroup || isUpdatingScrapingTagGroup
          }
        />
      )}
    </Modal>
  )
}

ScrapingTagGroupEditorModal.propTypes = {
  scrapingTagGroup: PropTypes.object,
  refetchScrapingTagGroups: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

ScrapingTagGroupEditorModal.defaultProps = {
  scrapingTagGroup: undefined,
}

export default ScrapingTagGroupEditorModal
